import {Modal, Textarea} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useState} from 'react';
import type {ComplianceLetterSectionDto} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {
  useGenerateComplianceLetterMutation,
  useLazyGetComplianceLetterDraftQuery,
} from 'src/redux/queries/inspection';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useDocumentDownload} from 'src/shared/hooks/useDocumentDownload';

export interface ComplianceLetterDialogProps {
  inspectionId: number;
  opened: boolean;
  onClose: () => void;
}

/**
 * Edit-then-generate dialog for the letter of compliance. The edited section
 * bodies post back for an on-demand PDF that is never stored server-side.
 */
export const ComplianceLetterDialog: React.FC<ComplianceLetterDialogProps> = ({
  inspectionId,
  opened,
  onClose,
}) => {
  const {setAlert} = useAlert();
  const {download} = useDocumentDownload();
  const [sections, setSections] = useState<ComplianceLetterSectionDto[]>([]);
  const [fetchDraft, {isFetching, isError}] =
    useLazyGetComplianceLetterDraftQuery();
  const [generateLetter, {isLoading: isGenerating}] =
    useGenerateComplianceLetterMutation();

  useEffect(() => {
    if (!opened) {
      return;
    }
    const load = async () => {
      try {
        const draft = await fetchDraft(inspectionId).unwrap();
        setSections(draft.sections);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
    };
    void load();
  }, [opened, inspectionId, fetchDraft, setAlert]);

  const handleBodyChange = (key: string, body: string) => {
    setSections(prev =>
      prev.map(section => (section.key === key ? {...section, body} : section))
    );
  };

  const handleGenerate = async () => {
    const downloaded = await download(
      timeZone =>
        generateLetter({id: inspectionId, request: {sections}, timeZone}).unwrap(),
      `compliance-letter-${inspectionId}.pdf`
    );
    if (downloaded) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Letter of compliance"
      size="lg"
      centered>
      {isFetching ? (
        <Spinner loadingText="Loading draft..." />
      ) : isError ? (
        <ErrorAlert message="Couldn't load the letter draft. Close this dialog and try again." />
      ) : (
        <Stack gap="md">
          {sections.map(section => (
            <Textarea
              key={section.key}
              label={section.title}
              value={section.body}
              onChange={event =>
                handleBodyChange(section.key, event.currentTarget.value)
              }
              autosize
              minRows={3}
            />
          ))}
          <Group justify="end">
            <Button variant="subtle" color="black" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              loading={isGenerating}
              disabled={sections.length === 0}>
              Generate PDF
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
